/** biome-ignore-all lint/suspicious/noExplicitAny: allow any */
/** biome-ignore-all lint/style/useNamingConvention: allow snake case */
import {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {EditButton} from '~/common/components/EditButton/EditButton';
import {selectIsMealOptionsEventType} from '~/common/fabric/reducers/widgets/selectors';
import {selectIsHost} from '~/common/fabric/User/reducers/user';
import {isMealOptionsEventType} from '~/common/utils/event_utils';
import {GuestlistDataContextProvider} from '~/ViewInvitation/components/Guestlist/context/GuestlistDataContext';
import {useGuestlistDataContext} from '~/ViewInvitation/components/Guestlist/hooks/useGuestlistDataContext';
import {voteForPollOption} from '~/ViewInvitation/reducers/apiActions';
import {PollChoiceOverlay} from '~/ViewInvitation/routes/Landing/PollModule/PollChoiceOverlay';
import {selectWidgets} from '~/ViewInvitation/selectors/eventDetails';
import {selectEventType} from '~/ViewInvitation/selectors/eviteEvent';
import {selectPoll, selectPollOptions} from '~/ViewInvitation/selectors/poll';
import {selectRsvp} from '~/ViewInvitation/selectors/rsvp';
import type {ModuleProps} from '~/ViewInvitation/types/module';
import {trackPollVote} from '~/ViewInvitation/utils/analytics';
import {isPreview} from '~/ViewInvitation/utils/index';
import {Button} from '~library/atoms/Button';
import {Radio} from '~library/atoms/Radio';
import {Typography} from '~library/atoms/Typography';
import {LockLineIcon} from '~library/svg/LockLineIcon';
import {PollResult} from './PollResult';

import './PollModule.sass';

interface PollModuleProps extends ModuleProps {
  restored?: boolean;
  onPropagateRestore?: (patchedEventData: {widgets: any}) => void;
}

export interface Option {
  key: string | number;
  choice: string;
  response_count: string | number;
}

const Poll = ({
  preview = isPreview(),
  readonly = true,
  restored,
  onPropagateRestore,
  editBtnRef,
}: PollModuleProps) => {
  const dispatch = useDispatch();
  const poll = useSelector(selectPoll);
  const rsvp = useSelector(selectRsvp);
  const question = poll?.question;
  const pollId = poll?.id;
  const voted = poll?.voted;
  const userChoice = poll?.option_selected_by_user;
  const hasRSVPed = rsvp?.hasRsvpd;
  const {
    state: {guestlist},
  } = useGuestlistDataContext();

  const options = useMemo(
    () =>
      poll?.options?.map((o: any, i: number) => ({
        key: o.id || i,
        choice: o.option || '',
        response_count: o.response_count || '',
      })) ?? [],
    [poll?.options]
  );

  const pollOptions = useSelector(selectPollOptions);
  const isHost = useSelector(selectIsHost);
  const widgets = useSelector(selectWidgets);
  const eventType = useSelector(selectEventType);
  const showMealOptionsEventType = isMealOptionsEventType(eventType);
  const isMealOptions = useSelector(selectIsMealOptionsEventType);
  const showMealOptions = showMealOptionsEventType || isMealOptions;
  const [selectedChoice, setSelectedChoice] = useState<string | number | false>(false);
  const [initialState, setInitialState] = useState(true);
  const [changingChoice, setChangingChoice] = useState(false);
  const [pollOverlayChoice, setPollOverlayChoice] = useState('');
  const [pollOverlayOpen, setPollOverlayOpen] = useState(false);
  const [guestsWhoVotedThis, setGuestsWhoVotedThis] = useState<any[]>([]);

  const ctaWording = () => {
    if (initialState && isHost && !readonly) return 'Add your vote';
    if (!changingChoice) return 'Change Vote';
    return 'Vote';
  };

  const totalResponses = options.reduce((sum: number, o: any) => sum + (+o.response_count || 0), 0);
  const showResults = !preview && ((initialState && isHost) || !changingChoice);

  useEffect(() => {
    if (!userChoice) {
      setChangingChoice(true);
    }
    setSelectedChoice(userChoice ?? false);
  }, [userChoice]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  const submitPollVote = useCallback(() => {
    trackPollVote('polls_vote');
    dispatch(
      voteForPollOption(pollId, 'cast_vote', {option: selectedChoice}, () => {
        setChangingChoice(false);
      }) as any
    );
  }, [trackPollVote, voteForPollOption, pollId, selectedChoice]);

  const changeChoice = useCallback(() => {
    setChangingChoice(true);
    setInitialState(false);
  }, []);

  const openPollOverlay = useCallback(
    (choice: string) => {
      setGuestsWhoVotedThis(() => {
        const relevantGuests = pollOptions.filter(
          (userChoice: any) => userChoice.option === choice
        )[0].response_user_ids;
        const relevantGuestNames = [];
        for (let i = 0; i < relevantGuests.length; i++) {
          const filteredGuest = guestlist?.filter((x) => x.userId === relevantGuests[i]);
          if (filteredGuest?.length !== 0) {
            relevantGuestNames.push(filteredGuest[0]);
          }
        }
        return relevantGuestNames;
      });

      setPollOverlayChoice(choice);
      setPollOverlayOpen(true);
    },
    [pollOptions, guestlist]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    if (restored && preview && isHost) {
      // API call to restore module
      const patchedEventData = {
        widgets,
      };
      onPropagateRestore?.(patchedEventData);
    }
  }, [restored]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    !voted ? setChangingChoice(true) : setInitialState(false);
  }, []);

  const ctaIsSecondary = !readonly && ((initialState && isHost) || !changingChoice);
  const editable = preview && !readonly;
  return (
    <>
      <EditButton data-qa-id="edit-poll" ref={editBtnRef} hide={!editable} />
      <div className="view-invitation__poll">
        <div className="view-invitation__poll-header">
          <div className="view-invitation__title-with-icon-wrapper">
            <Typography
              variant="label2"
              data-qa-id={showMealOptions ? 'meal-options-module-title' : 'poll-module-title'}
              color="pebble"
            >
              {showMealOptions ? 'Meal options' : 'Poll'}
            </Typography>
          </div>
          {question && (
            <Typography variant="header6" data-qa-id="poll-question" semibold>
              {question}
            </Typography>
          )}
        </div>
        <div className="view-invitation__poll-body">
          {showResults
            ? options?.map((o: Option) => (
                <PollResult
                  option={o}
                  key={o.key}
                  userChoice={userChoice}
                  totalResponses={totalResponses}
                  preview={preview}
                  onClick={openPollOverlay}
                />
              ))
            : options?.map((o: Option) => (
                <Radio
                  className="view-invitation__poll-option"
                  onChange={() => setSelectedChoice(o.key)}
                  data-qa-id="poll-option"
                  checked={selectedChoice === o.key}
                  label={o.choice}
                  id={`pollOption_${o.key}`}
                  key={o.key}
                  disabled={preview || !hasRSVPed}
                />
              ))}
        </div>
        <div className="view-invitation__poll-footer">
          <Button
            data-qa-id={changingChoice ? 'submit-choice' : 'change-my-choice'}
            onClick={showResults ? changeChoice : submitPollVote}
            disabled={preview || !hasRSVPed || (!initialState && !selectedChoice)}
            variant={ctaIsSecondary ? 'secondary' : 'primary'}
            size="small"
            fullWidth
          >
            {ctaWording()}
          </Button>
          {!preview && !hasRSVPed && (
            <Typography
              variant="paragraph2"
              className="view-invitation__poll-footer__message"
              semibold
            >
              <LockLineIcon ratio={0.75} /> RSVP to vote
            </Typography>
          )}
        </div>
        <PollChoiceOverlay
          open={pollOverlayOpen}
          onClose={() => setPollOverlayOpen(false)}
          choice={pollOverlayChoice}
          guests={guestsWhoVotedThis}
        />
      </div>
    </>
  );
};

export const PollModule = memo(
  ({
    preview = isPreview(),
    readonly = true,
    restored,
    onPropagateRestore,
    editBtnRef,
  }: PollModuleProps) => (
    <GuestlistDataContextProvider>
      <Poll
        preview={preview}
        readonly={readonly}
        restored={restored}
        onPropagateRestore={onPropagateRestore}
        editBtnRef={editBtnRef}
      />
    </GuestlistDataContextProvider>
  )
);

PollModule.displayName = 'PollModule';
